import React, { useCallback, useMemo } from "react";
import { CheckCircle, Error } from "@mui/icons-material";
import { Box, Paper, Tooltip } from "@mui/material";
import classNames from "classnames";
import { groupBy } from "lodash";

import { LayerStatus } from "fond/hooks/useGetImportLayers";
import { ImportStatus, ISGLayerIds } from "fond/types";
import { LinearProgressWithLabel, StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";

import { isgInputLayers } from "./layers";

import { GroupLabel } from "./InputPanel.styles";

interface InputPanelProps {
  isReadOnly?: boolean;
  inputLayers: Map<string, LayerStatus>;
}

const InputPanel: React.FC<InputPanelProps> = ({ isReadOnly, inputLayers }) => {
  const { open } = useStackedNavigationContext();

  const isAnyUploading = Array.from(inputLayers.values()).some(
    (layer) => layer.status && ["CONVERTING", "IMPORTING", "BUILDING_TILES"].includes(layer.status)
  );

  const openInputLayerPanel = (id: string) => {
    open(id);
  };

  const getRightAdornment = ({ isError, isRequired, isComplete }: { isError: boolean; isRequired: boolean; isComplete: boolean }) => {
    if (isError) {
      return (
        <Tooltip title="Import failed">
          <Error color="error" sx={{ fontSize: 18 }} />
        </Tooltip>
      );
    }

    if (!isComplete && isRequired) {
      return (
        <Tooltip title="Required">
          <Error color="action" sx={{ fontSize: 18 }} />
        </Tooltip>
      );
    }

    if (isComplete) {
      return <CheckCircle color="success" sx={{ fontSize: 18 }} />;
    }

    return null;
  };

  const isDisabled = useCallback(
    (id: string) => {
      const hasBoundary = !!inputLayers.get(ISGLayerIds.boundary)?.layer;
      if (isReadOnly || isAnyUploading) return true;
      if (id !== ISGLayerIds.boundary && !hasBoundary) return true;

      return false;
    },
    [inputLayers, isAnyUploading, isReadOnly]
  );

  // Group layers by their group property
  const groupedLayers = useMemo(() => groupBy(isgInputLayers, "group"), []);

  return (
    <>
      {Object.entries(groupedLayers).map(([groupName, layers], index) => (
        <Box key={groupName}>
          <GroupLabel
            variant="caption"
            className={classNames({
              first: index === 0,
            })}
          >
            {groupName}
          </GroupLabel>
          {layers.map(({ key, icon, title, isRequired }) => {
            const layerStatus = inputLayers.get(key);
            const hasLayer = !!layerStatus?.layer;
            const isError = layerStatus?.status === ImportStatus.ERROR;
            const isComplete = hasLayer && layerStatus?.status === ImportStatus.COMPLETE;

            return (
              <StackedNavigationButton
                key={`isg-${key}-input`}
                data-testid={`isg-${key}-input`}
                icon={icon}
                title={title}
                onClick={() => openInputLayerPanel(key)}
                sx={{ m: 0, mt: 1 }}
                disabled={isDisabled(key)}
                checked={isComplete}
                rightElement={getRightAdornment({ isError, isComplete, isRequired: !!isRequired })}
              />
            );
          })}
        </Box>
      ))}

      {isAnyUploading && (
        <Paper variant="outlined" sx={{ py: 2, px: 2.75, mt: 2 }}>
          <LinearProgressWithLabel color="primary" variant="indeterminate" title="Importing layer" subtext="Processing..." />
        </Paper>
      )}
    </>
  );
};

export default InputPanel;
